import "jsoneditor-react/es/editor.min.css";

/* eslint-disable react/display-name */
import { yupResolver } from "@hookform/resolvers/yup";
import Ajv from "ajv";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner
} from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiChevronDown,
  BiSolidMinusCircle,
  BiSolidPlusCircle
} from "react-icons/bi";
import * as Yup from "yup";

import i18n from "../../../../i18n/config";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import HintBlock from "../../../common/HintBlock";
import PopoverWithAudio from "../../../common/PopoverWithAudio";
import NodeParentChanger from "../NodeParentChanger";
import ScenarioSide from "./scenario-side";
import DeleteButton from "../DeleteButton";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import { ScenarioSimpleTextInput } from "./scenario-simple-text-input";
import AddNoteButton from "../AddNoteButton";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import { v4 as uuidv4 } from "uuid";
import { selectUseId } from "../../../../features/auth/auth.slice";
import { useAppSelector } from "../../../../app/hook";

const popupModalCatchResponseAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622190239_catch_reponse_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101622190271_catch_reponse_fr.mp3"
};

const popupModalAddBodyAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622380637_json_body_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101622380656_json_body_fr.mp3"
};

const popupModalMapResponseAudioPath: { [key: string]: string } = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24101622580441_associate_values_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/2410162258045_associate_values_fr.mp3"
};

const CustomToggle = React.forwardRef(
  (
    {
      children,
      onClick
    }: { children: React.ReactNode; onClick: (e: any) => void },
    ref: any
  ) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="tw-h-11 tw-w-full tw-px-3 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-between tw-items-center tw-inline-flex"
    >
      {children}
      <BiChevronDown size={16} className="tw-text-gray-400 ms-2" />
    </button>
  )
);

export interface IFormData extends INoteForm {
  title: string;
  url: string;
  automationType?: string;
  properties?: Property[];
}

export interface Property {
  key: string;
  value: string;
}

const ajv = new Ajv({ allErrors: true, verbose: true, strict: false });

const editorSchema = {
  type: "object"
};

export const MacBotMakeWebHookForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();

  const [isDelete, setIsDelete] = useState(true);
  const [showMapGuide, setShowMapGuide] = useState(false);
  const userId = useAppSelector(selectUseId);

  const schema = Yup.object({
    title: Yup.string()
      .trim()
      .required(t("McBotHttpForm/scenario_http_request_title_required")),
    automationType: Yup.string().trim().optional(),
    url: Yup.string()
      .url()
      .matches(
        /^https:\/\//,
        t("McBotHttpForm/scenario_http_request_url_required")
      )
      .required(t("McBotHttpForm/scenario_http_request_url_required")),
    properties: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string()
            .trim()
            .required(t("McBotHttpForm/webhook_request_property_key_required")),
          value: Yup.string()
            .trim()
            .required(
              t("McBotHttpForm/webhook_request_property_value_required")
            )
        })
      )
      .optional()
      .default([]),
    ...getNoteSchema(t)
  });

  const cleanText = (text: string) => {
    return text
      .replace(/[\n\r\t]/g, "") // Remove \n, \r, \t
      .replace(/\s+/g, " ") // Replace multiple spaces with a single space
      .trim(); // Remove leading and trailing spaces
  };

  const {
    register,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    control,
    trigger
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? "",
      url: itemData?.url ?? "",
      automationType: itemData?.automationType ?? "",
      properties: itemData?.body ?? [],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: "",
          description: "",
          qc: false,
          author: userId
        }
      ]
    }
  });

  const {
    fields: PropertyFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: "properties"
  });

  const { fields: noteFields } = useFieldArray({
    control,
    name: "notes"
  });

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        url: data.url,
        type: BlockType.MakeWebHook,
        parent: itemData.parent,
        automationType: data.automationType,
        body: data.properties,
        title: data.title,
        notes: data.notes
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.MakeWebHook,
        parent: parentId,
        title: data.title,
        url: data.url,
        automationType: data.automationType,
        body: data.properties,
        notes: data.notes
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? "",
        url: itemData?.url ?? "",
        automationType: itemData?.automationType ?? "",
        properties: itemData?.body ?? [],
        notes: itemData?.notes
      });
    }
  }, [itemData]);

  useEffect(() => {
    if (config.metaData.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="lg" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowMakeWebHookForm}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <img
              src="https://cdn.1cdn.app/application/LOUISEBOT/25031114181643_make_02.png"
              alt="discover_make"
              className="tw-w-full tw-h-auto"
            />
          </Row>
          <Row>
            <HintBlock texts={["MacMakeCom/affiliationLink"]} />
            <a
              href="https://www.make.com/en/register?pc=louisebot"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://cdn.1cdn.app/application/LOUISEBOT/25031114181641_AD-728x90_–_1.png"
                alt="make_account"
                className="tw-w-full tw-h-auto"
              />
            </a>
          </Row>
          <Row>
            <Form.Group className="tw-w-full">
              <Form.Label
                htmlFor=""
                className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
              >
                {t("McBotHttpForm/key4")}
              </Form.Label>
              <Form.Control
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                maxLength={30}
                max={30}
                type="text"
                isInvalid={!!errors.title}
                {...register("title")}
              />
              <HintBlock texts={["McBotHttpForm/maxlenght"]} />
              <Form.Control.Feedback
                type="invalid"
                className="d-block font-poppins tw-mt-3 tw-text-sm"
              >
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row className="gap-2">
            <Col xs={12}>
              <InputGroup className="tw-relative">
                <Form.Label
                  htmlFor=""
                  className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
                >
                  {t("McBotHttpForm/key31")}
                </Form.Label>
                <ScenarioSimpleTextInput
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  placeholder="https://hook.make.com/..."
                  setValue={(text) => {
                    setValue("url", text?.replaceAll(/(\r\n|\n|\r)/gm, ""));

                    trigger("url").then(() => {
                      clearErrors("url");
                    });
                  }}
                  value={itemData?.url ?? getValues("url")}
                  contextMenuList={contextMenuList}
                  isInvalid={!!errors.url}
                />
                <Form.Control.Feedback
                  type="invalid"
                  className="d-block font-poppins"
                >
                  {errors?.url?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Row>

          {/*
          <div className="h-[0px] border border-gray-200"></div>
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("McBotHttpForm/key9")}
                </Form.Label>
                <Form.Select
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  {...register("automationType")}
                >
                  <option value={"ADD_ROW_SHEETS"}>
                    {t("McBotHttpForm/key28")}
                  </option>
                </Form.Select>
                {errors.automationType && (
                  <span className="tw-text-red-500 tw-text-sm tw-block">
                    {errors.automationType?.message}
                  </span>
                )}
              </Form.Group>
            </Col>
          </Row>*/}

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                    {t("McBotHttpForm/key27")}
                  </div>
                </div>

                {PropertyFields.map((field, index) => (
                  <Row
                    key={field.id}
                    className={`tw-mt-2 tw-items-start ${
                      index + 1 < PropertyFields.length ? "mb-3" : ""
                    } `}
                  >
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <Form.Label
                              htmlFor=""
                              className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
                            >
                              {t("McBotHttpForm/key29")}
                            </Form.Label>
                            <ScenarioSimpleTextInput
                              placeholder={t(
                                "McBotHttpForm/PropertyKey"
                              ).toString()}
                              setValue={(text) => {
                                setValue(`properties.${index}.key`, text);
                              }}
                              value={getValues(`properties.${index}.key`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.properties?.[index]?.key}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.properties?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <Form.Label
                              htmlFor=""
                              className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
                            >
                              {t("McBotHttpForm/key30")}
                            </Form.Label>
                            <ScenarioSimpleTextInput
                              placeholder={t("McBotHttpForm/key7").toString()}
                              setValue={(text) => {
                                setValue(`properties.${index}.value`, text);
                              }}
                              value={getValues(`properties.${index}.value`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.properties?.[index]?.value}
                            />
                            <Form.Control.Feedback
                              type="invalid"
                              className="tw-block"
                            >
                              {errors.properties?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <button
                        type="button"
                        className="tw-flex tw-flex-col tw-min-h-[116px] tw-h-full tw-flex-1 tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2"
                        onClick={() => headerRemove(index)}
                      >
                        <BiSolidMinusCircle
                          className="tw-text-red-400"
                          size={20}
                        />
                      </button>
                    </Col>
                  </Row>
                ))}

                <button
                  type="button"
                  onClick={() => headerAppend({ key: "", value: "" })}
                  className="tw-h-10 tw-w-full tw-pl-[15px] tw-pr-5 tw-py-2.5 tw-bg-white tw-rounded-[99px] tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex"
                >
                  <BiSolidPlusCircle className="tw-text-gray-400" />
                  <span className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("McBotHttpForm/key27")}
                  </span>
                </button>
              </div>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {isDelete && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>

        <NoteForm
          noteFormMethods={{ register, watch, errors, fields: noteFields }}
        />
      </ScenarioSide>

      <Modal
        show={showMapGuide}
        onHide={() => setShowMapGuide(false)}
        size="lg"
        centered
      >
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-4">
                <span className="tw-text-gray-400 tw-text-base tw-font-semibold tw-font-['Lexend'] tw-leading-normal">
                  {t("McBotHttpForm/key11")}
                  <PopoverWithAudio
                    data={{
                      contentText: t("McBotHttpForm/key20"),
                      audioUrl: popupModalCatchResponseAudioPath[i18n.language],
                      size: 20
                    }}
                  />
                </span>
              </div>

              <div className="tw-min-h-[217px] tw-justify-start tw-items-start tw-gap-2 tw-grid tw-grid-cols-12 tw-w-full">
                <div className="md:tw-col-span-4 tw-col-span-12 tw-flex tw-flex-col tw-h-full">
                  <div className="tw-overflow-auto tw-h-full tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-gray-200 tw-rounded-md tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                    <div className="tw-self-stretch justify-between tw-items-center tw-inline-flex">
                      <div className="tw-w-[219px] tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("McBotHttpForm/key12")}
                      </div>
                      <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      <pre>
                        {JSON.stringify(
                          {
                            users: [{ name: "john", email: "john@mail.com" }]
                          },
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
                <div className="md:tw-col-span-8 tw-col-span-12 tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start items-center tw-gap-4 tw-flex">
                  <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start tw-items-start tw-gap-4 tw-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                      <div className="tw-self-stretch tw-h-[217px] tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                        <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                          <div className="tw-text-gray-500 tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t("McBotHttpForm/key13")}
                            <br />
                            users.0.email
                          </div>
                          <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
